import type { Customer } from "@api/types";
import {
  ChatAlt2Icon,
  CursorClickIcon,
  DeviceMobileIcon,
  MailIcon,
} from "@heroicons/react/outline";
import { cn, formatDateRelative, formatPhoneNumber, getCustomerName } from "@lib/utils";
import { ArrowRight, Calendar, Import, LucideClipboardCheck, UserCircle } from "lucide-react";
import Link from "next/link";
import { type ReactNode } from "react";
import { buttonVariants } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/cell-table";
import { CustomerMessageButton } from "@/ui/components/customer-message-link";
import { TableLoading } from "@/ui/elements";
import { CustomerAvatar } from "./customer-avatar";
import { EmptyContactValue } from "./empty-contact-value";
import { OptedOutBadge } from "./opted-out-badge";
import { CustomerSourceBadge } from "./source-badge";

interface ContactsTableProps {
  customers?: Customer[];
  isLoading?: boolean;
  emptyState?: ReactNode;
}

export function ContactsTable(props: ContactsTableProps) {
  return (
    <Table>
      <TableHeader className="bg-background shadow-base sticky top-0 z-10 border-b dark:border-border border-transparent">
        <TableRow>
          <TableHead>
            <div className="flex items-center gap-1">
              <UserCircle className="w-4 h-4" />
              Person
            </div>
          </TableHead>
          <TableHead>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Date added
            </div>
          </TableHead>
          <TableHead>
            <div className="flex items-center gap-1">
              <Import className="w-4 h-4" />
              Source
            </div>
          </TableHead>
          <TableHead>
            <div className="flex items-center gap-1">
              <DeviceMobileIcon className="w-4 h-4" />
              Phone number
            </div>
          </TableHead>
          <TableHead>
            <div className="flex items-center gap-1">
              <MailIcon className="w-4 h-4" />
              Email
            </div>
          </TableHead>
          <TableHead>
            <div className="flex items-center gap-1">
              <LucideClipboardCheck className="w-4 h-4" />
              Opt-in status
            </div>
          </TableHead>
          <TableHead>
            <div className="flex items-center gap-1">
              <CursorClickIcon className="w-4 h-4" />
              Actions
            </div>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {props.isLoading && !props.customers ? (
          <TableRow className="hover:bg-background">
            <TableCell colSpan={7} className="p-0">
              <TableLoading className="border-x-0 border-t border-b-0 rounded-none" />
            </TableCell>
          </TableRow>
        ) : props.customers?.length ? (
          props.customers.map((customer) => (
            <TableRow key={customer.id}>
              <TableCell className="min-w-[220px]">
                <Link className="group flex items-center gap-3" href={`/contacts/${customer.id}`}>
                  <CustomerAvatar customer={customer} />
                  <span className="group-hover:underline">{getCustomerName(customer)}</span>
                </Link>
              </TableCell>
              <TableCell>{formatDateRelative(customer.createdAt)}</TableCell>
              <TableCell>
                <CustomerSourceBadge customer={customer} />
              </TableCell>
              <TableCell>
                <EmptyContactValue
                  value={customer.phoneNumber ? formatPhoneNumber(customer.phoneNumber) : undefined}
                />
              </TableCell>
              <TableCell className="max-w-56 truncate">
                <EmptyContactValue value={customer.email} />
              </TableCell>
              <TableCell>
                <OptedOutBadge customer={customer} />
              </TableCell>
              <TableCell className="min-w-[240px]">
                <div className="flex gap-2">
                  <CustomerMessageButton
                    size="xs"
                    variant="outline"
                    className="shadow-none"
                    customer={customer}
                  >
                    <ChatAlt2Icon className="inline-block w-4 h-4 mr-1" />
                    Message
                  </CustomerMessageButton>
                  <Link
                    href={`/contacts/${customer.id}`}
                    className={cn(
                      buttonVariants({ size: "xs", variant: "outline" }),
                      "shadow-none",
                    )}
                  >
                    <ArrowRight className="inline-block w-4 h-4 mr-1" />
                    View Profile
                  </Link>
                </div>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={7} className="py-12 text-center">
              {props.emptyState}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
